<script setup lang="ts">
import LoadingButton from "@/components/LoadingButton.vue"
import type { ShopOrderApprovalTask } from "@/model/order/shop-order-approval-task"
import ShopOrderTable from "@/pages/Tasks/Tasks/internal/ShopOrderTable.vue"
import type { TasksProps } from "@/pages/Tasks/Tasks/tasks-props"
import { defineTranslations } from "@/utils/defineTranslations"
import { useHttpClient, useLoadingState } from "@/vf"
import { toRefs } from "vue"

const props = defineProps<TasksProps<ShopOrderApprovalTask>>()

const emit = defineEmits<{
    (e: "updated"): void
}>()

const { task } = toRefs(props)
// Some shop areas get their own wording, every other shop area shares the generic description
const textVariant = props.task.shopOrder!.shopConfiguration!.textVariant!
const http = useHttpClient()
const approveLoading = useLoadingState()
const rejectLoading = useLoadingState()

async function approve() {
    approveLoading.state = true
    await http.post(`/order/shop/${task.value.shopOrder?.shopConfiguration?.id}/${task.value.shopOrder?.id}/approve`)
    approveLoading.state = false
    emit("updated")
}

async function reject() {
    rejectLoading.state = true
    await http.post(
        `/order/shop/${task.value.shopOrder?.shopConfiguration?.id}/${task.value.shopOrder?.id}/reject-approval`,
    )
    rejectLoading.state = false
    emit("updated")
}

const trans = defineTranslations({
    "description.generic.title": {
        de: "Bestellung freigeben",
        en: "Approve order",
    },
    "description.generic.text": {
        de: "Die Bestellung wird von einer Office Mitarbeiterin geprüft.",
        en: "The order is being reviewed by an office member.",
    },
    "description.workshops_camps.title": {
        de: "Buchung freigeben",
        en: "Approve booking",
    },
    "description.workshops_camps.text": {
        de: "Die Buchung wird von einer Office Mitarbeiterin geprüft.",
        en: "The booking is being reviewed by an office member.",
    },
    "description.tickets.title": {
        de: "Ticketbestellung freigeben",
        en: "Approve ticket order",
    },
    "description.tickets.text": {
        de: "Die Ticketbestellung wird von einer Office Mitarbeiterin geprüft.",
        en: "The ticket order is being reviewed by an office member.",
    },
    "description.private_camps.title": {
        de: "Private-Camp-Buchung freigeben",
        en: "Approve private camp booking",
    },
    "description.private_camps.text": {
        de: "Die Private-Camp-Buchung wird von einer Office Mitarbeiterin geprüft.",
        en: "The private camp booking is being reviewed by an office member.",
    },
    approve: {
        de: "Freigeben",
        en: "Approve",
    },
    reject: {
        de: "Ablehnen",
        en: "Reject",
    },
})
</script>

<template>
    <div class="p-5">
        <b>{{ trans(`description.${textVariant}.title`) }}</b>
        <div>{{ trans(`description.${textVariant}.text`) }}</div>

        <ShopOrderTable
            :shop-order-id="task.shopOrder?.id!"
            :shop-configuration-id="task.shopOrder?.shopConfiguration?.id!"
            :assignments-editable="task.state === 'open'"
        />

        <div class="d-flex justify-content-end gap-2 mt-4" v-if="task.state === 'open' && viewMode === 'admin'">
            <LoadingButton :loading="rejectLoading" class="btn btn-danger" @click="reject">
                {{ trans("reject") }}
            </LoadingButton>
            <LoadingButton :loading="approveLoading" class="btn btn-success" @click="approve">
                {{ trans("approve") }}
            </LoadingButton>
        </div>
    </div>
</template>
